import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import Modal from '@/components/ui/Modal';
import { APIRoutes } from '@/services/api.routes';
import { MimeTypeIcon, type MimeType } from '@/components/ui/MimeTypeIcon';
import type { VideoID } from '@/models';

interface UploadVideoAttachmentModalProps {
  videoId: VideoID;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingAttachments?: number; // Number of existing attachments to calculate next order
}

// https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/MIME_types/Common_types
// Must be synced with backend
// see models.MimeType.ValidVideoAttachment
const ALLOWED_VIDEO_MIMETYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/csv',
  'text/plain',
  'text/markdown',
  'application/json',
  'application/xml',
  'text/xml',
  'application/rtf',
  'text/rtf',
  'application/zip',
  'application/x-zip-compressed',
  'application/vnd.ms-excel',
  'application/msword',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.presentation',
  'audio/mpeg',
  'audio/mp4',
  'audio/x-m4a',
  'audio/aac',
  'audio/wav',
  'audio/x-wav',
  'audio/ogg',
  'audio/flac',
];

// file.type is empty or generic when the OS has no handler registered (e.g. .xlsx without Office), so the extension is checked as well
const ALLOWED_VIDEO_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.gif', '.webp',
  '.pdf', '.csv', '.txt', '.md', '.json', '.xml', '.rtf', '.zip',
  '.xls', '.xlsx', '.xlsm', '.doc', '.docx', '.ppt', '.pptx',
  '.odt', '.ods', '.odp',
  '.mp3', '.m4a', '.aac', '.wav', '.ogg', '.flac',
];

const isAllowedAttachment = (file: File) => {
  if (ALLOWED_VIDEO_MIMETYPES.includes(file.type)) return true;
  const name = file.name.toLowerCase();
  return ALLOWED_VIDEO_EXTENSIONS.some(ext => name.endsWith(ext));
};


const UploadVideoAttachmentModal: React.FC<UploadVideoAttachmentModalProps> = ({
  videoId,
  isOpen,
  onClose,
  onSuccess,
  existingAttachments = 0
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [filename, setFilename] = useState('');
  const [description, setDescription] = useState('');
  const [order, setOrder] = useState(existingAttachments);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (selectedFile: File) => {
    if (!isAllowedAttachment(selectedFile)) {
      setError('Dateityp wird nicht unterstützt');
      return;
    }

    setFile(selectedFile);
    setFilename(selectedFile.name);
    setError(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!file) {
      setError('Bitte wählen Sie eine Datei aus');
      return;
    }

    if (!filename.trim()) {
      setError('Bitte geben Sie einen Dateinamen ein');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      await APIRoutes.uploadVideoAttachment(
        videoId,
        file,
        filename.trim(),
        description.trim() || undefined,
        order
      );

      onSuccess();
    } catch (err) {
      console.error('Error uploading attachment:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      setFilename('');
      setDescription('');
      setOrder(existingAttachments);
      setError(null);
      onClose();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Anhang hochladen"
      icon={<FontAwesomeIcon icon={faUpload} className="w-5 h-5 text-blue-600" />}
      maxWidth="lg"
      closeDisabled={uploading}
    >
      <form onSubmit={handleSubmit} className="p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* File Upload Area */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Datei auswählen
          </label>
          {!file ? (
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <FontAwesomeIcon icon={faUpload} className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                Datei hierher ziehen oder klicken zum Auswählen
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                PDF, Bilder, Office-Dokumente, CSV, Text, Audio, ZIP
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <MimeTypeIcon mimeType={file.type as MimeType} className="w-6 h-6" />
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-gray-100">{file.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {formatFileSize(file.size)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 cursor-pointer"
              >
                Entfernen
              </button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileInputChange}
            className="hidden"
            accept={[...ALLOWED_VIDEO_MIMETYPES, ...ALLOWED_VIDEO_EXTENSIONS].join(',')}
          />
        </div>

        {/* Filename Input */}
        <div className="mb-4">
          <label htmlFor="filename" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Dateiname <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="filename"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Angezeigter Dateiname"
            required
          />
        </div>

        {/* Description Input */}
        <div className="mb-4">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Beschreibung (optional)
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Kurze Beschreibung des Anhangs"
          />
        </div>

        {/* Order Input */}
        <div className="mb-6">
          <label htmlFor="order" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Reihenfolge
          </label>
          <input
            type="number"
            id="order"
            value={order}
            onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
            min="0"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            placeholder="Reihenfolge (0 = erste Position)"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Niedrigere Zahlen werden zuerst angezeigt (Standard: {existingAttachments})
          </p>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={uploading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={!file || !filename.trim() || uploading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {uploading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Wird hochgeladen...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} className="w-4 h-4" />
                Hochladen
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default UploadVideoAttachmentModal;
